import { useState, useCallback } from "react";
import {
	getEvaluationState,
	createEvaluation,
	changeEvaluationMode,
	updateEvaluationProgress,
} from "../api/evaluation-api";
import type { EvaluationState, CurrentUserContext } from "../types/evaluation.type";
import type { PanelMode } from "../types/panel.type";

interface UseEvaluationOptions {
	rfxId: number | undefined;
	rfxSubmissionId?: number;
}

interface UseEvaluationResult {
	currentStep: number;
	currentUserContext: CurrentUserContext | null;
	error: string | null;
	evaluationState: EvaluationState | null;
	hasStarted: boolean;
	isLoading: boolean;
	fetchEvaluationState: () => Promise<void>;
	startEvaluation: (panelMode?: PanelMode) => Promise<EvaluationState | null>;
	changeMode: (newMode: PanelMode) => Promise<EvaluationState | null>;
	updateProgress: (fields: { current_step?: number; panel_mode?: string }) => Promise<void>;
}

export const useEvaluation = ({
	rfxId,
	rfxSubmissionId,
}: UseEvaluationOptions): UseEvaluationResult => {
	const [evaluationState, setEvaluationState] =
		useState<EvaluationState | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchEvaluationState = useCallback(async (): Promise<void> => {
		if (!rfxId) {
			return;
		}
		setIsLoading(true);
		setError(null);
		try {
			const state = await getEvaluationState(rfxId, rfxSubmissionId);
			setEvaluationState(state);
		} catch {
			setError("Failed to load evaluation state");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId, rfxSubmissionId]);

	const handleCreateEvaluation =
		useCallback(async (panelMode?: PanelMode): Promise<EvaluationState | null> => {
			if (!rfxId) {
				return null;
			}
			setIsLoading(true);
			setError(null);
			try {
				const state = await createEvaluation(rfxId, panelMode);
				setEvaluationState(state);
				return state;
			} catch {
				setError("Failed to create evaluation");
				return null;
			} finally {
				setIsLoading(false);
			}
		}, [rfxId]);

	const handleChangeMode = useCallback(
		async (newMode: PanelMode): Promise<EvaluationState | null> => {
			if (!rfxId || !evaluationState?.id) {
				return null;
			}
			setError(null);
			try {
				await changeEvaluationMode(rfxId, evaluationState.id, newMode);
				// Re-fetch full state so current_user_context is preserved
				await fetchEvaluationState();
				return evaluationState;
			} catch (err: unknown) {
				const axiosErr = err as { response?: { data?: { message?: string } } };
				setError(axiosErr?.response?.data?.message ?? "Failed to change evaluation mode");
				return null;
			}
		},
		[rfxId, evaluationState?.id, fetchEvaluationState]
	);

	const updateProgress = useCallback(
		async (fields: { current_step?: number; panel_mode?: string }): Promise<void> => {
			if (!rfxId) {
				return;
			}
			try {
				const state = await updateEvaluationProgress(rfxId, fields);
				setEvaluationState((prev) => ({
					...state,
					currentUserContext: state.currentUserContext ?? prev?.currentUserContext ?? null,
				}));
			} catch {
				// Silent fail — progress save is non-critical
			}
		},
		[rfxId]
	);

	return {
		evaluationState,
		hasStarted: evaluationState?.hasStarted ?? false,
		currentStep: evaluationState?.currentStep ?? 0,
		currentUserContext: evaluationState?.currentUserContext ?? null,
		isLoading,
		error,
		fetchEvaluationState,
		startEvaluation: handleCreateEvaluation,
		changeMode: handleChangeMode,
		updateProgress,
	};
};
